import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '@root/src/stores';
import PickupStage from './stages/PickupStage';
import DeliveryStage from './stages/DeliveryStage';
import useFilteredStores from './hooks/useFilteredStores';
import useListScrollIntoView from '@customHooks/useListScrollIntoView';
import useStorePlacemarks from './hooks/useStorePlacemarks';
import { PICKUP_ID, SHOP_NAME_KEYS } from './constants';
import { expressDeliveryStorage } from '@constants/delivery';
import { PICKUP_MAP_CENTER } from '@constants/geo';
import { useRegionAwareCenter } from '@customHooks/useRegionAwareCenter';
import type { GuestDeliveryPickupModalProps } from './types';
import './index.scss';

const GuestDeliveryPickupModal: FC<GuestDeliveryPickupModalProps> = observer(
  ({
    pickups,
    triggerChangePickupStore,
    setShowModal,
    mapState,
    isHideDelivery,
    availableQuantity,
    isShouldAutoSwitchPickupStore = true,
    isShowAvailableQuantity = false
  }) => {
    const { cartStore, configStore } = useStores();
    const { openedShops } = cartStore;
    const { store_id, delivery_id } = cartStore.cart;
    const cashback_for_pickup = configStore.data?.cashback_for_pickup;

    const isPickupCart = isShouldAutoSwitchPickupStore
      ? delivery_id === PICKUP_ID
      : true;

    const [searchValue, setSearchValue] = useState('');
    const [storeId, setStoreId] = useState<number>(store_id || 0);
    const [isShowMap, setIsShowMap] = useState(true);

    const filteredShops = useFilteredStores({
      stores: openedShops,
      searchValue,
      nameKeys: SHOP_NAME_KEYS,
      isShowAvailableQuantity,
      availableQuantity
    });

    const { elRefs } = useListScrollIntoView({
      itemCount: filteredShops?.length ?? 0,
      activeKey: storeId
    });

    const placemarks = useStorePlacemarks({
      stores: filteredShops,
      activeStoreId: storeId,
      onSelect: setStoreId,
      isShowAvailableQuantity,
      availableQuantity
    });

    const selectedShop = useMemo(
      () => ({
        ...pickups.find(item => item.id === storeId),
        ...openedShops.find(item => item.id === storeId)
      }),
      [pickups, openedShops, storeId]
    );

    const regionAwareCenter = useRegionAwareCenter(selectedShop?.geo);
    const mapCoordsShops =
      Array.isArray(regionAwareCenter) &&
      regionAwareCenter.length >= 2 &&
      regionAwareCenter.every(coord => Number.isFinite(Number(coord)))
        ? regionAwareCenter.map(Number)
        : PICKUP_MAP_CENTER;

    const closeModal = useCallback((): void => {
      setShowModal(false);
    }, [setShowModal]);

    const onSubmit = useCallback((): void => {
      if (isPickupCart) {
        triggerChangePickupStore(storeId);
      }
      setShowModal(false);
    }, [isPickupCart, triggerChangePickupStore, storeId, setShowModal]);

    const onChangeType = useCallback(
      (id: number) => () => {
        const { cart } = cartStore;
        const cartParams =
          id === PICKUP_ID && cart.lastCartStoreId
            ? { storeId: cart.lastCartStoreId }
            : {};

        cartStore
          .updateCartStage({ deliveryId: id, ...cartParams })
          .then(() => {
            expressDeliveryStorage.set(false);
            setIsShowMap(true);
          });
      },
      [cartStore]
    );

    return (
      <div className='modal-pickup-notLogged'>
        {isPickupCart ? (
          <PickupStage
            isHideDelivery={isHideDelivery}
            isShowAvailableQuantity={isShowAvailableQuantity}
            availableQuantity={availableQuantity}
            cashbackForPickup={cashback_for_pickup}
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            filteredShops={filteredShops}
            selectedShop={selectedShop}
            storeId={storeId}
            setStoreId={setStoreId}
            elRefs={elRefs}
            placemarks={placemarks}
            mapCoordsShops={mapCoordsShops}
            isShowMap={isShowMap}
            setIsShowMap={setIsShowMap}
            onChangeType={onChangeType}
            closeModal={closeModal}
            onSubmit={onSubmit}
            address={mapState.address}
          />
        ) : (
          <DeliveryStage
            isHideDelivery={isHideDelivery}
            mapState={mapState}
            onChangeType={onChangeType}
            closeModal={closeModal}
            onSubmit={onSubmit}
          />
        )}
      </div>
    );
  }
);

export default GuestDeliveryPickupModal;
